import {
  Data,
  ErrorLevel,
  RowHook,
  TableHook,
} from 'react-spreadsheet-import/types/types';
import { object, z } from 'zod';
import { addValidationError, parseNumberWithCommas } from '../utils';
import { Scope1And2CategoryConfig } from './config';
import { Scope1And2ImportRow } from './types';

export const SCOPE_1_2_ERROR_MESSAGES = {
  INVALID_QUANTITY: 'Quantity must be a valid number',
  NEGATIVE_QUANTITY: 'Quantity must be a number greater than zero',
  DUPLICATE_ROW:
    'Duplicate row — this site, type and time period already appears in another row',
} as const;

const NUMBER_REGEX = /^-?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$/;

/**
 * Setup-step form schema: the user chooses whether site and time period are
 * assigned from a column in the sheet (`auto`) or picked once up-front
 * (`single`). Mobile combustion may also choose `none` for site.
 */
export const scope1And2ImportSchema = object({
  periodMethod: z.string().min(1),
  timePeriodUuid: z.string().min(1).optional(),
  siteMethod: z.string().min(1),
  siteUuid: z.string().min(1).optional(),
}).superRefine((data, ctx) => {
  if (data.periodMethod === 'single' && !data.timePeriodUuid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['timePeriodUuid'],
      message: 'Please select a time period',
    });
  }
  if (data.siteMethod === 'single' && !data.siteUuid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['siteUuid'],
      message: 'Please select a site',
    });
  }
});

export type Scope1And2ImportFormValues = z.infer<typeof scope1And2ImportSchema>;

const sanitizeRow = (row: Scope1And2ImportRow): Scope1And2ImportRow => ({
  ...row,
  quantity: row.quantity && row.quantity.trim(),
});

/**
 * Row-level validation. Reference fields (site, fuel, gas, unit, time period,
 * source) are select columns whose options are the report's own reference data,
 * so the importer already blocks unresolved values and missing required selects
 * — a time period outside the report's reporting periods can't map to an option
 * and is rejected. This hook adds the numeric quantity checks that the backend
 * validates only loosely.
 */
export const useScope1And2RowValidator = (): RowHook<
  keyof Scope1And2ImportRow
> => {
  return (row, addError) => {
    const sanitizedRow = sanitizeRow(row as Scope1And2ImportRow);
    const { quantity } = sanitizedRow;

    const reportError = (
      field: keyof Scope1And2ImportRow,
      level: ErrorLevel,
      message: string
    ) =>
      addValidationError<Scope1And2ImportRow>(addError, field, level, message);

    if (quantity) {
      if (!NUMBER_REGEX.test(quantity)) {
        reportError(
          'quantity',
          'error',
          SCOPE_1_2_ERROR_MESSAGES.INVALID_QUANTITY
        );
      } else {
        const quantityValue = parseNumberWithCommas(quantity);
        if (isNaN(quantityValue) || quantityValue <= 0) {
          reportError(
            'quantity',
            'error',
            SCOPE_1_2_ERROR_MESSAGES.NEGATIVE_QUANTITY
          );
        }
      }
    }

    return sanitizedRow as Data<keyof Scope1And2ImportRow>;
  };
};

/**
 * Table-level validation that flags duplicate rows. Two rows are duplicates
 * when they resolve to the same activity record — i.e. they share the same
 * site, fuel/gas/source and time period. The backend keys an activity record
 * on exactly these fields and would silently overwrite the earlier row with
 * the later one, so we surface the clash here instead and block submission.
 *
 * The identity fields mirror each category's backend activity key; `unit` and
 * `quantity` are deliberately excluded (a repeated period with a different
 * quantity is still a duplicate). When the site or time period is chosen
 * up-front its column is absent and constant across rows, so it drops out of
 * the key — the remaining fields still identify duplicates within that single
 * site/period. The error is attached to the visible identity cells (falling
 * back to quantity if none are shown) so the user can see what to fix.
 */
export const useScope1And2TableValidator = (
  config: Scope1And2CategoryConfig,
  { excludeSite = false, excludePeriod = false } = {}
): TableHook<keyof Scope1And2ImportRow> => {
  const identityFields: (keyof Scope1And2ImportRow)[] = [
    ...(excludeSite ? [] : (['site'] as const)),
    ...(config.hasFuel ? (['fuel'] as const) : []),
    ...(config.hasGas ? (['gas'] as const) : []),
    ...(config.hasSource ? (['source'] as const) : []),
    ...(excludePeriod ? [] : (['timePeriod'] as const)),
  ];
  const errorFields = identityFields.length ? identityFields : ['quantity'];

  return (table, addError) => {
    const rowIndicesByKey = new Map<string, number[]>();

    table.forEach((row, index) => {
      const key = identityFields
        .map((field) => String((row as Scope1And2ImportRow)[field] ?? ''))
        .join('|');
      rowIndicesByKey.set(key, [...(rowIndicesByKey.get(key) ?? []), index]);
    });

    rowIndicesByKey.forEach((indices) => {
      if (indices.length < 2) return;
      indices.forEach((index) =>
        errorFields.forEach((field) =>
          addError(index, field as keyof Scope1And2ImportRow, {
            level: 'error',
            message: SCOPE_1_2_ERROR_MESSAGES.DUPLICATE_ROW,
          })
        )
      );
    });

    return table;
  };
};
